import { Award, FileDown, Gift, ImageOff, MessageCircle, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emptyCertificate = {
  enabled: false,
  template_type: "default",
  text_color: "#171717",
  name_y: 50,
  course_y: 64,
  date_y: 76,
};

export const CourseExtrasEditor = ({ course, setCourse }) => {
  const lessons = course.modules.flatMap((module) => module.lessons);
  const certificate = course.certificate || emptyCertificate;
  const updateBonus = (id, changes) => setCourse((current) => ({
    ...current,
    bonuses: current.bonuses.map((bonus) => bonus.id === id ? { ...bonus, ...changes } : bonus),
  }));
  const updateGroup = (id, changes) => setCourse((current) => ({
    ...current,
    whatsapp_groups: current.whatsapp_groups.map((group) => group.id === id ? { ...group, ...changes } : group),
  }));
  const updateCertificate = (changes) => setCourse((current) => ({
    ...current,
    certificate: { ...(current.certificate || emptyCertificate), ...changes },
  }));
  const upload = async (event, callback) => {
    const file = event.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/files", form);
      callback(data);
      event.target.value = "";
      toast.success("Plik przesłany");
    } catch {
      toast.error("Nie udało się przesłać pliku");
    }
  };

  return <section className="extras-editor" data-testid="course-extras-editor">
    <div className="extras-heading">
      <span>ELASTYCZNE USTAWIENIA PRODUKTU</span>
      <h2>Bonusy, społeczność i certyfikat</h2>
    </div>
    <div className="extras-grid">
      <article className="extras-panel">
        <header>
          <Gift/>
          <div><h3>Materiały bonusowe</h3><p>Tekst, plik, wideo lub zewnętrzny link.</p></div>
          <button
            onClick={() => setCourse((current) => ({
              ...current,
              bonuses: [...(current.bonuses || []), {
                id: crypto.randomUUID(),
                title: "Nowy bonus",
                description: "",
                type: "text",
                content: "",
                url: "",
                file_id: null,
                image_file_id: null,
                unlock_condition: "immediate",
                lesson_id: null,
              }],
            }))}
            data-testid="add-bonus-button"
            aria-label="Dodaj bonus"
          ><Plus/></button>
        </header>
        {(course.bonuses || []).map((bonus) => <section className="extra-item" key={bonus.id} data-testid={`bonus-editor-${bonus.id}`}>
          <div className="extra-row">
            <input value={bonus.title} onChange={(event) => updateBonus(bonus.id, { title: event.target.value })} placeholder="Nazwa bonusu"/>
            <button onClick={() => setCourse((current) => ({ ...current, bonuses: current.bonuses.filter((item) => item.id !== bonus.id) }))} aria-label="Usuń bonus"><Trash2/></button>
          </div>
          <div className={`bonus-image-control ${bonus.image_file_id ? "ready" : ""}`}>
            <label className="extra-upload bonus-image-upload">
              <Upload/> {bonus.image_file_id ? "Zmień zdjęcie bonusu" : "Dodaj zdjęcie bonusu"}
              <input type="file" accept="image/*" onChange={(event) => upload(event, (data) => updateBonus(bonus.id, { image_file_id: data.id }))}/>
            </label>
            {bonus.image_file_id ? <button
              className="remove-bonus-image"
              type="button"
              onClick={() => updateBonus(bonus.id, { image_file_id: null })}
              data-testid={`remove-bonus-image-${bonus.id}`}
            ><ImageOff/> Usuń zdjęcie</button> : null}
          </div>
          <textarea value={bonus.description || ""} onChange={(event) => updateBonus(bonus.id, { description: event.target.value })} placeholder="Opis"/>
          <div className="extra-row">
            <select value={bonus.type} onChange={(event) => updateBonus(bonus.id, { type: event.target.value })}>
              <option value="text">Tekst</option>
              <option value="file">Plik</option>
              <option value="video">Wideo</option>
              <option value="link">Link</option>
            </select>
            <select value={bonus.unlock_condition} onChange={(event) => updateBonus(bonus.id, { unlock_condition: event.target.value })}>
              <option value="immediate">Od razu</option>
              <option value="after_lesson">Po lekcji</option>
              <option value="course_complete">Po ukończeniu kursu</option>
            </select>
          </div>
          {bonus.unlock_condition === "after_lesson" ? <select value={bonus.lesson_id || ""} onChange={(event) => updateBonus(bonus.id, { lesson_id: event.target.value })}>
            <option value="">Wybierz lekcję</option>
            {lessons.map((lesson) => <option value={lesson.id} key={lesson.id}>{lesson.title}</option>)}
          </select> : null}
          {bonus.type === "text"
            ? <textarea value={bonus.content || ""} onChange={(event) => updateBonus(bonus.id, { content: event.target.value })} placeholder="Treść bonusu"/>
            : bonus.type === "file"
              ? <label className="extra-upload"><FileDown/> {bonus.file_id ? "Plik dodany" : "Prześlij plik"}<input type="file" onChange={(event) => upload(event, (data) => updateBonus(bonus.id, { file_id: data.id }))}/></label>
              : <input value={bonus.url || ""} onChange={(event) => updateBonus(bonus.id, { url: event.target.value })} placeholder="https://"/>}
        </section>)}
      </article>

      <article className="extras-panel">
        <header>
          <MessageCircle/>
          <div><h3>Grupy WhatsApp</h3><p>Kilka grup z osobnym włącznikiem.</p></div>
          <button
            onClick={() => setCourse((current) => ({
              ...current,
              whatsapp_groups: [...(current.whatsapp_groups || []), { id: crypto.randomUUID(), name: "Nowa grupa", url: "https://chat.whatsapp.com/", enabled: true }],
            }))}
            data-testid="add-whatsapp-group"
            aria-label="Dodaj grupę WhatsApp"
          ><Plus/></button>
        </header>
        {(course.whatsapp_groups || []).map((group) => <section className="extra-item" key={group.id} data-testid={`whatsapp-editor-${group.id}`}>
          <div className="extra-row">
            <input value={group.name} onChange={(event) => updateGroup(group.id, { name: event.target.value })}/>
            <label className="mini-switch">AKTYWNA<input type="checkbox" checked={group.enabled} onChange={(event) => updateGroup(group.id, { enabled: event.target.checked })}/></label>
            <button onClick={() => setCourse((current) => ({ ...current, whatsapp_groups: current.whatsapp_groups.filter((item) => item.id !== group.id) }))} aria-label="Usuń grupę"><Trash2/></button>
          </div>
          <input value={group.url} onChange={(event) => updateGroup(group.id, { url: event.target.value })} placeholder="https://chat.whatsapp.com/..."/>
        </section>)}
      </article>

      <article className="extras-panel certificate-panel">
        <header>
          <Award/>
          <div><h3>Certyfikat</h3><p>Własny PNG/JPG lub PDF z danymi ucznia.</p></div>
          <label className="mini-switch">AKTYWNY<input type="checkbox" checked={certificate.enabled} onChange={(event) => updateCertificate({ enabled: event.target.checked })} data-testid="certificate-enabled"/></label>
        </header>
        {certificate.enabled ? <section className="extra-item">
          <select value={certificate.template_type} onChange={(event) => updateCertificate({ template_type: event.target.value })} data-testid="certificate-template-type">
            <option value="default">Szablon MARSEM</option>
            <option value="image">PNG / JPG</option>
            <option value="pdf">PDF</option>
          </select>
          {certificate.template_type !== "default" ? <label className="extra-upload"><Upload/> {certificate.template_file_id ? "Szablon dodany" : "Prześlij szablon"}<input type="file" accept={certificate.template_type === "pdf" ? ".pdf" : "image/*"} onChange={(event) => upload(event, (data) => updateCertificate({ template_file_id: data.id }))} data-testid="certificate-template-file"/></label> : null}
          <label>KOLOR TEKSTU<input type="color" value={certificate.text_color || "#171717"} onChange={(event) => updateCertificate({ text_color: event.target.value })}/></label>
          <div className="position-sliders">
            <label>Imię {certificate.name_y}%<input type="range" min="20" max="80" value={certificate.name_y || 50} onChange={(event) => updateCertificate({ name_y: Number(event.target.value) })}/></label>
            <label>Kurs {certificate.course_y}%<input type="range" min="30" max="90" value={certificate.course_y || 64} onChange={(event) => updateCertificate({ course_y: Number(event.target.value) })}/></label>
            <label>Data {certificate.date_y}%<input type="range" min="40" max="95" value={certificate.date_y || 76} onChange={(event) => updateCertificate({ date_y: Number(event.target.value) })}/></label>
          </div>
        </section> : null}
      </article>
    </div>
  </section>;
};
